import { apiResourceUrl } from "@ploy-lib/core";
import { BaseResource, paramsToString } from "./BaseResource";
import { ProductResource } from "./ProductResource";
import { LimitApplicationDataResource, LimitApplicationDataCustomerResource } from "./LimitApplicationResource";
import { LimitRedistributionResource } from "./LimitRedistributionResource";
import { BeneficialOwnerResource } from "./BeneficialOwnerResource";
import { ApplicationResource } from "./ApplicationResource";
import startCase from "lodash/startCase";
import { isTemplatePage, TemplateDataSource, CustomGuiFieldHandling } from "@ploy-lib/types";
import { Endpoint, Entity, Resource, schema } from "@rest-hooks/rest";
import { EmbeddedProductFieldResource } from "./EmbeddedProductFieldResource";
import { ApplicationPropertiesResource } from "./ApplicationPropertiesResource";
import { FinancingInformationResource } from "./FinancingInformationResource";
import { ApplicationMortgageERegistrationResource } from "./ApplicationMortgageERegistrationResource";
import { PropertyERegistrationOverviewResource } from "./PropertyERegistrationOverviewResource";
class TemplateTableColumnEntity extends Entity {
    constructor() {
        super(...arguments);
        this.formTemplateTableColumnId = "";
        this.name = "";
        this.tableType = "";
        this.showSum = undefined;
        this.editable = undefined;
        this.isMultipleSelect = undefined;
        this.optionValues = undefined;
        this.allOptionValues = undefined;
        this.optionSource = undefined;
        this.isGroupingColumn = undefined;
        this.hrefLink = "";
        this.groupLabel = "";
        this.footerGroupLabel = "";
    }
    pk() {
        return this.formTemplateTableColumnId;
    }
}
class TemplateFieldEntity extends Entity {
    constructor() {
        super(...arguments);
        this.formTemplateFieldId = "";
        this.namespace = undefined;
        this.name = "";
        this.storageName = undefined;
        this.initialFieldValue = undefined;
        this.modifier = undefined;
        this.label = undefined;
        this.tooltip = undefined;
        this.placeholder = undefined;
        this.prefix = undefined;
        this.valueSuffix = undefined;
        this.textAreaRows = undefined;
        this.maxLength = undefined;
        this.modalText = undefined;
        this.disabled = undefined;
        this.literal = undefined;
        this.textAlign = undefined;
        this.alignWithPrevious = undefined;
        this.alwaysVisible = undefined;
        this.alwaysEnabled = undefined;
        this.alwaysDisabled = undefined;
        this.useFullWidthForHelperText = undefined;
        this.isRequired = undefined;
        this.hiddenLabel = undefined;
        this.width = undefined;
        this.role = undefined;
        this.renderAs = undefined;
        this.variant = undefined;
        this.renderAsLiteral = undefined;
        this.literalVariant = undefined;
        this.horizontal = undefined;
        this.color = undefined;
        this.errorDisplay = undefined;
        this.errorDisplayLiteral = undefined;
        this.hideErrorWhenLiteral = undefined;
        this.hideForMobile = undefined;
        this.hideIfDisabled = undefined;
        this.fullWidth = undefined;
        this.mailto = undefined;
        this.linkIsUrl = undefined;
        this.alignCenterInParent = undefined;
        this.boldText = undefined;
        this.italicText = undefined;
        this.helperText = undefined;
        this.suffix = undefined;
        this.margin = undefined;
        this.multiple = undefined;
        this.manual = undefined;
        this.justifyContent = undefined;
        this.icon = undefined;
        this.uppercaseLabel = undefined;
        this.allowEmpty = undefined;
        this.hiddenIfNoChoice = undefined;
        this.searchable = undefined;
        this.emptyEqualsZero = undefined;
        this.href = undefined;
        this.target = undefined;
        this.to = undefined;
        this.optionSource = undefined;
        this.search = undefined;
        this.save = undefined;
        this.click = undefined;
        this.open = undefined;
        this.globalEvent = undefined;
        this.formatString = undefined;
        this.canResetWriteLocked = undefined;
        this.optionPairCategoryName = undefined;
        this.useFieldSeperator = undefined;
        this.requireCustomGuiField = CustomGuiFieldHandling.Require;
    }
    pk() {
        return this.formTemplateFieldId;
    }
}
class TemplateSectionEntity extends Entity {
    constructor() {
        super(...arguments);
        this.formTemplateSectionId = "";
        this.sectionTemplateId = undefined;
        this.title = undefined;
        this.internalDescription = undefined;
        this.style = undefined;
        this.layout = undefined;
        this.fields = [];
        this.literal = undefined;
        this.tabGroup = undefined;
        this.tableColumns = [];
        this.expansionGroup = undefined;
        this.group = undefined;
        this.expansionTitle = undefined;
        this.displaySectionTitle = undefined;
        this.showWhenCollapsed = undefined;
        this.enableExpansion = undefined;
        this.expandInitially = undefined;
        this.expansionTitleUppercase = true;
        this.outerWidthSM = undefined;
        this.groupBackgroundColor = undefined;
        this.innerWidthSM = undefined;
        this.useSeparator = undefined;
        this.elevation = undefined;
        this.square = undefined;
        this.outline = undefined;
        this.outlineWidth = undefined;
        this.hideForMobile = undefined;
        this.hideIfDisabled = undefined;
        this.nextLabel = undefined;
        this.prevLabel = undefined;
        this.mainText = undefined;
        this.helperText = undefined;
        this.showStatus = undefined;
        this.showUpdateSupplier = undefined;
        this.hideSectionIfNoVisibleFields = undefined;
        this.standardMargin = undefined;
        this.centerText = undefined;
        this.showDialogButtons = undefined;
        this.hideSignerList = undefined;
        this.hideSignerEditing = undefined;
        this.showAlternativeMessage = undefined;
        this.fullWidthOpenButton = undefined;
        this.showUpdateCoSigner = undefined;
        this.useResourceBinding = undefined;
        this.tableType = undefined;
        this.tableIsEditable = undefined;
        this.mergeColumnValues = undefined;
        this.submitOnRowChange = undefined;
        this.borderlessTable = undefined;
        this.tableFilter = [];
        this.multiselect = undefined;
        this.canDeleteRow = undefined;
        this.canAddNewRows = undefined;
        this.editButtonAtBottom = undefined;
        this.hideEditAndSaveIcons = undefined;
        this.moveFirstItemToTitle = undefined;
    }
    pk() {
        return this.formTemplateSectionId;
    }
}
TemplateSectionEntity.schema = {
    fields: [TemplateFieldEntity],
    tableColumns: [TemplateTableColumnEntity]
};
class TemplatePanelEntity extends Entity {
    constructor() {
        super(...arguments);
        this.formPanelId = "";
        this.title = undefined;
        this.sections = [];
        this.initialTab = undefined;
        this.elevation = undefined;
        this.square = undefined;
        this.hideForMobile = undefined;
        this.desktopViewForMobile = undefined;
        this.minHeight = undefined;
        this.literal = undefined;
    }
    pk() {
        return this.formPanelId;
    }
}
TemplatePanelEntity.schema = {
    sections: [TemplateSectionEntity]
};
class TemplatePageEntity extends Entity {
    constructor() {
        super(...arguments);
        this.formPageId = "";
        this.pageTitle = undefined;
        this.pageUri = undefined;
        this.layout = undefined;
        this.prevLabel = undefined;
        this.nextLabel = undefined;
        this.submitLabel = undefined;
        this.panels = {};
        this.description = undefined;
        this.displayInStepper = undefined;
        this.headerIconName = undefined;
        this.hideFunctionButtons = undefined;
        this.reduceFormHeightForLaptops = undefined;
        this.maxWidth = undefined;
        this.gtmPageEvent = undefined;
    }
    pk() {
        return this.formPageId;
    }
}
TemplatePageEntity.schema = {
    panels: new schema.Values(TemplatePanelEntity)
};
class TemplatePageDeprecatedEntity extends Entity {
    constructor() {
        super(...arguments);
        this.formPageId = "";
        this.pageTitle = undefined;
        this.pageUri = undefined;
        this.layout = undefined;
        this.prevLabel = undefined;
        this.nextLabel = undefined;
        this.submitLabel = undefined;
        this.sections = [];
        this.summary = [];
        this.summaryLayout = undefined;
        this.gtmPageEvent = undefined;
    }
    pk() {
        return this.formPageId;
    }
}
TemplatePageDeprecatedEntity.schema = {
    sections: [TemplateSectionEntity],
    summary: [TemplateFieldEntity]
};
class FormTemplateResource extends BaseResource {
    constructor() {
        super(...arguments);
        this.formTemplateId = "";
        this.name = "";
        this.isInUse = undefined;
        this.description = undefined;
        this.category = undefined;
        this.pages = [];
        this.dataSource = TemplateDataSource.CalcRules;
        this.resource = undefined;
    }
    pk() {
        return this.formTemplateId?.toString();
    }
    static url(urlParams) {
        const url = super.url(urlParams);
        const queryParams = {
            customerContext: urlParams.customerContext,
            isMobile: urlParams.isMobile,
            refreshCounter: urlParams.refreshCounter
        };
        return `${url}${paramsToString(queryParams)}`;
    }
}
FormTemplateResource.schema = {
    pages: [
        new schema.Union({ page: TemplatePageEntity, deprecated: TemplatePageDeprecatedEntity }, p => {
            if (isTemplatePage(p))
                return "page";
            return "deprecated";
        })
    ]
};
FormTemplateResource.urlTemplates = [
    `${ApplicationResource.urlRoot}/{applicationNumber}/form-templates/{formContext}/{context}`,
    `${ProductResource.urlRoot}/{productExternalCode}/form-templates/{formContext}/{context}`,
    `${apiResourceUrl("global/form-templates")}/{formContext}`
];
FormTemplateResource.urlRoot = apiResourceUrl("form-templates");
export { FormTemplateResource };
// Only useful for a form template editor.
// Use a custom class in order to avoid dirtying the original form template state.
class UnmergedFormTemplateResource extends FormTemplateResource {
    static detail() {
        const endpoint = super.detail();
        return endpoint.extend({
            url: urlParams => {
                const url = endpoint.url(urlParams);
                return `${url}?skipMerge=true`;
            }
        });
    }
}
UnmergedFormTemplateResource.urlTemplates = [];
export { UnmergedFormTemplateResource };
export class StatefulFormTemplateStateEntity extends Entity {
    constructor() {
        super(...arguments);
        this.applicationSessionId = "";
        this.openClosedGroups = {};
        this.tableColumnOptionValues = [];
    }
    pk(parent, key) {
        return this.applicationSessionId ?? "";
    }
}
export const StatefulFormTemplateStateEndpoint = new Endpoint((request) => Resource.fetch(apiResourceUrl("form-templates/state-dependent-values"), {
    method: "POST",
    body: JSON.stringify(request)
}), {
    schema: StatefulFormTemplateStateEntity,
    dataExpiryLength: 5 * 60 * 1000,
    key: (req) => `LOAD form-template-state-dependent-values ${req.applicationSessionId}`
});
export const dataSourceResources = {
    ProductResource,
    FormTemplateResource,
    LimitApplicationDataResource,
    LimitApplicationDataCustomerResource,
    LimitRedistributionResource,
    EmbeddedProductFieldResource,
    ApplicationPropertiesResource,
    FinancingInformationResource,
    BeneficialOwnerResource,
    ApplicationMortgageERegistrationResource,
    PropertyERegistrationOverviewResource
};
const resourceLabelKey = {
    FormTemplateResource: "name",
    ProductResource: "name",
    LimitApplicationDataResource: "applicationId",
    LimitApplicationDataCustomerResource: "customerId",
    LimitRedistributionResource: "customerId",
    FinancingInformationResource: "applicationId",
    ApplicationPropertiesResource: "applicationId",
    EmbeddedProductFieldResource: "applicationId",
    BeneficialOwnerResource: "__id",
    ApplicationMortgageERegistrationResource: "applicationId",
    PropertyERegistrationOverviewResource: "applicationId"
};
const resourceGroupKey = {
    FormTemplateResource: "category",
    ProductResource: "category",
    LimitApplicationDataResource: "applicationId",
    LimitApplicationDataCustomerResource: "customerId",
    LimitRedistributionResource: "customerId",
    FinancingInformationResource: "applicationId",
    ApplicationPropertiesResource: "applicationId",
    EmbeddedProductFieldResource: "applicationId",
    BeneficialOwnerResource: "__id",
    ApplicationMortgageERegistrationResource: "applicationId",
    PropertyERegistrationOverviewResource: "applicationId"
};
export const getResourceLabel = (s) => startCase(s.replace(/Resource$/, ""))
    .toLowerCase()
    .replace(/^./, match => match.toUpperCase());
export const createGetResourceInstanceLabel = (resource) => {
    const key = resourceLabelKey[resource];
    return (instance) => instance[key]?.toString() ?? "";
};
export const createGetResourceInstanceGroup = (resource) => {
    const key = resourceGroupKey[resource];
    if (!key)
        return undefined;
    return (instance) => instance[key]?.toString() ?? "";
};
//# sourceMappingURL=FormTemplateResource.js.map